/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import { useState, useEffect } from 'react';
import { getAuth, updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase.js';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAlert from 'components/MDAlert';

// Settings page components
import FormField from 'composites/FormField';

// Data
import Header from '../Header';

function BasicInfo(): JSX.Element {
  const auth = getAuth();
  const user = auth.currentUser;

  const [fullName, setFullName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    // Set initial full name from current user
    if (user?.displayName) {
      setFullName(user.displayName);
    }
  }, [user]);

  const handleUpdateName = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Update display name in Firebase Auth
      await updateProfile(user, {
        displayName: fullName,
      });

      setSuccess('Name updated successfully');
    } catch (err) {
      console.error('Error updating name:', err);
      setError('Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card id="basic-info" sx={{ overflow: 'visible' }}>
      <Header />
      <MDBox p={3}>
        <MDTypography variant="h5">Basic Info</MDTypography>
      </MDBox>

      {error && (
        <MDBox px={3} mb={2}>
          <MDAlert color="error">{error}</MDAlert>
        </MDBox>
      )}

      {success && (
        <MDBox px={3} mb={2}>
          <MDAlert color="success">{success}</MDAlert>
        </MDBox>
      )}

      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField
              label="Full Name"
              placeholder="Enter your full name"
              value={fullName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFullName(e.target.value)
              }
            />
          </Grid>
        </Grid>

        <MDBox mt={3} />

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
        >
          <MDBox ml="auto">
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={handleUpdateName}
              disabled={loading || !fullName}
            >
              Update Name
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default BasicInfo;
